import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	CommonState,
	MULTI_SESSION_LIMIT_MESSAGE_CONTEXT,
	MultiSessionLimit,
	MultiSessionLimitAction,
	MultiSessionLimitConfigService,
	MultiSessionLimitResponse,
	MultiSessionLimitSelector,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { sessionLimitAmountValidator } from "@obg/ngx.ui.common";
import { pickBy } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import {
	HgcSessionLimitFormGroup,
	HgcSessionLimitPeriod,
	HgcSessionLimitPeriodTranslationsEnum,
	HgcSessionLimitSuggestionAmounts,
	HgcSessionLimitTemplate,
} from "./hgc-session-limit.container.model";

const TRANSLATION_PREFIX = "hgc.multi-session-limit";
const HGC_SESSION_LIMIT_ALERT_ID = "HGC_SESSION_LIMIT_ALERT_ID";

@Component({
	selector: "obg-m-hgc-session-limit-container",
	templateUrl: "./hgc-session-limit.container.html",
	styleUrls: ["./hgc-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class HgcSessionLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-hgc-session-limit") hostClass = true;

	readonly sessionLimitPeriodControls = HgcSessionLimitPeriod;

	isSettingBusy = false;
	currentSessionLimit: HgcSessionLimitTemplate | undefined;
	upcomingSessionLimit: HgcSessionLimitTemplate | undefined;

	formGroup: HgcSessionLimitFormGroup = this.formBuilder.group({
		sessionLimitAmount: this.formBuilder.control("", [sessionLimitAmountValidator]),
		sessionLimitPeriod: this.formBuilder.control(HgcSessionLimitPeriod.Daily, [Validators.required]),
	}) as HgcSessionLimitFormGroup;
	isSessionLimitEdited = false;
	placeholder = "24";
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private alertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private multiSessionLimitConfigService: MultiSessionLimitConfigService,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {}

	ngOnInit() {
		this.store.dispatch(this.multiSessionLimitAction.get());

		const sessionLimits$ = this.store.pipe(
			select(this.multiSessionLimitSelector.get()),
			tap((sessionLimit) => {
				this.currentSessionLimit = undefined;
				this.upcomingSessionLimit = undefined;
				if (sessionLimit?.current) {
					this.currentSessionLimit = this.mapSessionLimits(sessionLimit.current);
				}

				if (sessionLimit?.upcoming) {
					this.upcomingSessionLimit = this.mapSessionLimits(sessionLimit.upcoming);
				}
			})
		);

		const sessionLimitUpdate$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				tap((message) => {
					this.isSettingBusy = false;
					this.formGroup.setValue({
						sessionLimitAmount: "",
						sessionLimitPeriod: HgcSessionLimitPeriod.Daily,
					});
					if (message[0].type === MessageType.Success) {
						this.updateSuccess("update_success");
						return;
					}
					this.updateFail();
				})
			);

		const sessionLimitPeriodControlChanges$ = this.formGroup.controls.sessionLimitPeriod.valueChanges.pipe(
			tap((value: HgcSessionLimitPeriod) => {
				this.formGroup.controls.sessionLimitAmount.reset();
				this.placeholder =
					HgcSessionLimitSuggestionAmounts[
						HgcSessionLimitPeriod[value] as keyof typeof HgcSessionLimitSuggestionAmounts
					].toString();
			})
		);

		const isBusy$ = this.store.pipe(
			select(this.multiSessionLimitSelector.isBusy()),
			tap((isBusy) => (this.isSettingBusy = isBusy))
		);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formGroup?.reset({
					sessionLimitAmount: "",
					sessionLimitPeriod: HgcSessionLimitPeriod.Daily,
				});
			})
		);

		this.data$$ = merge(
			sessionLimits$,
			sessionLimitUpdate$,
			sessionLimitPeriodControlChanges$,
			isBusy$,
			resetForm$
		).subscribe(() => this.changeDetector.markForCheck());

		this.isFormDirty$$ = this.formGroup.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.formGroup.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	editSessionLimit() {
		this.isSessionLimitEdited = true;
	}

	submitSessionLimit() {
		const messageKey = this.multiSessionLimitConfigService.getMultiSessionLimitSuffixed()
			? `${TRANSLATION_PREFIX}.confirmation-caption.${HgcSessionLimitPeriod[
					this.formGroup.controls.sessionLimitPeriod.value
				].toLowerCase()}`
			: `${TRANSLATION_PREFIX}.confirmation-caption`;

		this.alertService.open(
			{
				id: HGC_SESSION_LIMIT_ALERT_ID,
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: messageKey,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(
								this.multiSessionLimitAction.update(
									this.getMultiSessionLimitValues(
										this.formGroup.controls.sessionLimitAmount.value,
										this.formGroup.controls.sessionLimitPeriod.value as HgcSessionLimitPeriod
									)
								)
							);
							this.isSettingBusy = true;
							this.changeDetector.markForCheck();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	private updateFail() {
		this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private updateSuccess(suffix: string) {
		this.snackBarService
			.openSnackBar$(
				{
					message: `${TRANSLATION_PREFIX}.m_multi_session_limit_${suffix}`,
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private mapSessionLimits(sessionLimit: MultiSessionLimitResponse) {
		const sessionLimitPeriod: MultiSessionLimit = pickBy(
			sessionLimit,
			(value, key: keyof MultiSessionLimitResponse) => value !== 0 && key !== "activeFrom"
		);
		return { ...this.getSessionLimitPeriodTranslation(sessionLimitPeriod), activeFrom: sessionLimit.activeFrom };
	}

	private getSessionLimitPeriodTranslation(sessionLimitPeriod: MultiSessionLimit) {
		const key = Object.keys(sessionLimitPeriod)[0] as HgcSessionLimitPeriodTranslationsEnum;
		const period = Object.keys(HgcSessionLimitPeriodTranslationsEnum)
			.find(
				(x: keyof typeof HgcSessionLimitPeriodTranslationsEnum) =>
					HgcSessionLimitPeriodTranslationsEnum[x] === key
			)!
			.toLowerCase();
		return { limitValue: sessionLimitPeriod[key]!, limitPeriod: period };
	}

	private getMultiSessionLimitValues(sessionLimitAmount: number, sessionLimitPeriod: HgcSessionLimitPeriod) {
		const periodName = HgcSessionLimitPeriod[
			sessionLimitPeriod
		] as keyof typeof HgcSessionLimitPeriodTranslationsEnum;
		const period = HgcSessionLimitPeriodTranslationsEnum[periodName];
		return { [period]: sessionLimitAmount };
	}
}
